import PacedModulesService from "../../../../API/organization/PacedModulesService";
import {
    buildPacedDetailModulePayload,
    buildPacedSceneUpdatePayload,
    getPacedDtoHide,
    parsePacedDetailModuleDto,
} from "./pacedDetailModule";

export {getPacedDtoHide};

/** Тот же разбор позы, что у paced-detail-modules (anchor + axes). */
export const parsePacedModuleDto = (dto) => parsePacedDetailModuleDto(dto);

export const getPacedModuleId = (row) => {
    const fromNested = row?.pacedModule?.id ?? row?.pacedModuleId;
    if (fromNested != null && fromNested !== "" && Number(fromNested) !== 0) {
        return fromNested;
    }
    return null;
};

const hasRealId = (value) => value != null && value !== "" && Number(value) !== 0;

const pacedModuleByModuleIdCache = new Map();

export const invalidateEmbeddedModulePlacementCache = (dependModuleId) => {
    if (dependModuleId == null || dependModuleId === "") return;
    pacedModuleByModuleIdCache.delete(String(dependModuleId));
};

export const invalidateAllEmbeddedModulePlacementCache = () => {
    pacedModuleByModuleIdCache.clear();
};

/** Id для DELETE/PUT paced-modules/{id} — только id записи размещения (PacedModuleDto.id). */
export const resolveEmbeddedPacedDeleteId = ({
    moduleKey = null,
    inst = null,
    pacedEmbeddedIdByKey = {},
}) => {
    if (moduleKey != null && hasRealId(pacedEmbeddedIdByKey[moduleKey])) {
        return pacedEmbeddedIdByKey[moduleKey];
    }
    if (hasRealId(inst?.pacedModuleId)) return inst.pacedModuleId;
    const fromDto = getPacedModuleId({pacedModule: inst?.pacedModule});
    if (hasRealId(fromDto)) return fromDto;
    return null;
};

export const fetchPacedModulePlacement = async (pacedModuleId) => {
    if (pacedModuleId == null || pacedModuleId === "") return null;
    try {
        const response = await PacedModulesService.getById(pacedModuleId);
        return parsePacedModuleDto(response?.data) ?? null;
    } catch (err) {
        console.warn("module-3d: paced-modules get failed", err);
        return null;
    }
};

/** GET paced-modules/{id} — полный DTO размещения. */
export const fetchPacedModuleDtoById = async (pacedModuleId) => {
    if (!hasRealId(pacedModuleId)) return null;
    try {
        const response = await PacedModulesService.getById(pacedModuleId);
        return response?.data ?? null;
    } catch (err) {
        const status = err?.response?.status;
        if (status === 404 || status === 204) return null;
        console.warn("module-3d: paced-modules get failed", err);
        return null;
    }
};

export const fetchPacedModuleByModuleId = async (moduleId, {bypassCache = false} = {}) => {
    if (moduleId == null || moduleId === "") return null;
    const cacheKey = String(moduleId);
    if (!bypassCache && pacedModuleByModuleIdCache.has(cacheKey)) {
        return pacedModuleByModuleIdCache.get(cacheKey);
    }
    try {
        const response = await PacedModulesService.getByModuleId(moduleId);
        const data = response?.data ?? null;
        pacedModuleByModuleIdCache.set(cacheKey, data);
        return data;
    } catch (err) {
        const status = err?.response?.status;
        if (status === 404 || status === 204) {
            pacedModuleByModuleIdCache.set(cacheKey, null);
            return null;
        }
        console.warn("module-3d: paced-modules by-module-id failed", err);
        return null;
    }
};

/** DELETE paced-modules/{pacedModuleId} — снять размещение со сцены (не удаление модуля из состава). */
export const deleteEmbeddedModulePlacement = async (pacedModuleId, dependModuleId = null) => {
    if (!hasRealId(pacedModuleId)) return false;
    try {
        await PacedModulesService.delete(pacedModuleId);
        invalidateEmbeddedModulePlacementCache(dependModuleId);
        return true;
    } catch (err) {
        const status = err?.response?.status;
        if (status === 204) {
            invalidateEmbeddedModulePlacementCache(dependModuleId);
            return true;
        }
        console.warn("module-3d: paced-modules delete failed", err);
        return false;
    }
};

/** POST paced-modules?moduleId= — поза из клика по сцене (как paced-detail-modules). */
export const createEmbeddedModulePlacement = async (dependModuleId, {
    localPosition,
    axes,
    faceFlip = false,
}) => {
    if (!hasRealId(dependModuleId)) {
        throw new Error("paced-modules POST: invalid depend module id");
    }
    const payload = buildPacedDetailModulePayload({
        localPosition,
        axes,
        faceFlip,
        existingDto: null,
        forCreate: true,
    });
    try {
        const response = await PacedModulesService.save(dependModuleId, payload);
        const data = response?.data;
        if (data == null || typeof data !== "object") {
            throw new Error("Empty paced-modules POST response");
        }
        if (!hasRealId(data.id)) {
            throw new Error("POST response has no paced-modules id");
        }
        pacedModuleByModuleIdCache.set(String(dependModuleId), data);
        return data;
    } catch (err) {
        console.warn("module-3d: paced-modules create failed", err);
        throw err;
    }
};

/** DTO с бэка для PUT (id точек и variables). */
export const resolveEmbeddedPacedExistingDto = (inst) => {
    if (!inst || typeof inst !== "object") return null;
    const fromInst = inst.pacedModule?.raw ?? inst.pacedModule;
    if (fromInst && typeof fromInst === "object" && fromInst.anchorPoint != null) {
        return fromInst;
    }
    const fromRow = inst.row?.pacedModule;
    if (fromRow && typeof fromRow === "object") return fromRow;
    return null;
};

/** PATCH paced-modules/{id}/hide — body: boolean. */
export const updatePacedModuleHidden = async ({
    pacedModuleId,
    hide,
    existingDto = null,
    dependModuleId = null,
}) => {
    if (!hasRealId(pacedModuleId)) return null;
    const prev = existingDto && typeof existingDto === "object" ? existingDto : {};
    try {
        const response = await PacedModulesService.setHide(pacedModuleId, hide);
        const data = response?.data ?? null;
        const nextDto = data != null && typeof data === "object" && data.anchorPoint != null
            ? data
            : {
                ...prev,
                ...(hasRealId(pacedModuleId) ? {id: pacedModuleId} : {}),
                hide: Boolean(hide),
            };
        if (dependModuleId != null && dependModuleId !== "") {
            pacedModuleByModuleIdCache.set(String(dependModuleId), nextDto);
        }
        return nextDto;
    } catch (err) {
        console.warn("module-3d: paced-modules hidden update failed", err);
        return null;
    }
};

export const hideAllPacedEmbeddedModules = async (moduleId) => {
    if (moduleId == null || moduleId === "") return false;
    try {
        await PacedModulesService.showOrHideAllDepend(moduleId, true);
        invalidateAllEmbeddedModulePlacementCache();
        return true;
    } catch (err) {
        console.warn("module-3d: paced-modules show-or-hide-all-depend failed", err);
        return false;
    }
};

export const showAllPacedEmbeddedModules = async (moduleId) => {
    if (moduleId == null || moduleId === "") return false;
    try {
        await PacedModulesService.showOrHideAllDepend(moduleId, false);
        invalidateAllEmbeddedModulePlacementCache();
        return true;
    } catch (err) {
        console.warn("module-3d: paced-modules show-or-hide-all-depend failed", err);
        return false;
    }
};

export const hideAllPacedEmbeddedExcept = async (moduleId, pacedModuleId) => {
    if (moduleId == null || moduleId === "" || !hasRealId(pacedModuleId)) return false;
    try {
        await PacedModulesService.hideAllExcept(pacedModuleId, moduleId, true);
        invalidateAllEmbeddedModulePlacementCache();
        return true;
    } catch (err) {
        console.warn("module-3d: paced-modules hide-all-except failed", err);
        return false;
    }
};

export const updateEmbeddedModuleScenePose = async ({
    pacedModuleId,
    localPosition,
    axes,
    faceFlip = false,
    existingDto = null,
    dependModuleId = null,
}) => {
    if (!hasRealId(pacedModuleId)) return null;
    const prev = existingDto && typeof existingDto === "object" ? existingDto : {};
    const body = buildPacedSceneUpdatePayload({
        localPosition,
        axes,
        faceFlip,
        existingDto: prev,
        pacedId: pacedModuleId,
    });
    try {
        const response = await PacedModulesService.update(pacedModuleId, body);
        const data = response?.data ?? null;
        if (data != null && dependModuleId != null && dependModuleId !== "") {
            pacedModuleByModuleIdCache.set(String(dependModuleId), data);
        }
        return data;
    } catch (err) {
        console.warn("module-3d: paced-modules update failed", err);
        return null;
    }
};
